import React, { useCallback, useEffect, useState } from "react";
import { ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { gbp } from "../../lib/format";
import { formatApiError } from "../../lib/errors";
import { CompareLinesDialog } from "./CompareLinesDialog";

function Diff({ d, testid }) {
  if (!d) return <span className="text-ink-4">—</span>;
  const over = d.amount > 0;   // quote above ours
  const color = d.amount === 0 ? "#3A5A47" : over ? "#B03A2E" : "#3A5A47";
  return (
    <span className="font-mono tnum" style={{ color, fontSize: 11.5 }} data-testid={testid}>
      {d.amount > 0 ? "+" : ""}{gbp(d.amount)}{d.pct !== null && d.pct !== undefined ? ` (${d.pct > 0 ? "+" : ""}${d.pct}%)` : ""}
    </span>
  );
}

/** LT-38 — our labour / labour + materials for the picked lines, ex and inc O&P, against the quote. */
export function QuoteComparePanel({ tenderId, quote, onChanged }) {
  const [cmp, setCmp] = useState(null);
  const [pick, setPick] = useState(false);
  const qid = quote.id;

  const load = useCallback(() => {
    api.get(`/tenders/${tenderId}/subby-quotes/${qid}/comparison`).then((r) => setCmp(r.data)).catch((e) => toast.error(formatApiError(e, "Failed to load comparison")));
  }, [tenderId, qid]);
  useEffect(load, [load]);

  const save = async (ids) => {
    const r = await api.put(`/tenders/${tenderId}/subby-quotes/${qid}/compare-lines`, { line_ids: ids });
    setCmp(r.data); onChanged?.();
    toast.success(`Comparing against ${ids.length} line${ids.length === 1 ? "" : "s"}`);
  };

  if (!cmp) return <div className="mt-3 text-ink-3" style={{ fontSize: 12 }}>Loading comparison…</div>;
  const rows = [
    { key: "labour", label: "Our labour", f: cmp.labour },
    { key: "labour_materials", label: "Our labour + materials", f: cmp.labour_materials },
  ];
  return (
    <div className="mt-3 rounded-md border border-line bg-surface-2/30 p-3" data-testid={`subby-quote-${qid}-compare`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-ink-2" style={{ fontSize: 12 }} data-testid={`subby-quote-${qid}-compare-status`}>
          {cmp.selected_count === 0
            ? "No tender lines picked yet."
            : <>Against <span className="font-medium text-ink">{cmp.selected_count} line{cmp.selected_count === 1 ? "" : "s"}</span>
                {cmp.missing_line_ids.length > 0 && <span className="ml-2 rounded px-1.5 py-0.5" style={{ background: "#FEF3E2", color: "#7A4C0A", fontSize: 10.5 }} data-testid={`subby-quote-${qid}-compare-missing`}>{cmp.missing_line_ids.length} line{cmp.missing_line_ids.length === 1 ? "" : "s"} no longer exist{cmp.missing_line_ids.length === 1 ? "s" : ""}</span>}
                <span className="text-ink-3"> · O&P = overhead {cmp.overhead_pct}% + profit {cmp.profit_pct}%</span></>}
        </div>
        <button type="button" onClick={() => setPick(true)} className="inline-flex items-center gap-1 h-7 px-2 rounded text-ink-2 hover:text-ink hover:bg-surface-2 border border-line" style={{ fontSize: 11 }} data-testid={`subby-quote-${qid}-compare-pick`}>
          <ListChecks size={11} /> {cmp.selected_count ? "Change lines" : "Pick lines"}
        </button>
      </div>
      {cmp.selected_count > 0 && (
        <table className="w-full mt-2" style={{ fontSize: 12 }} data-testid={`subby-quote-${qid}-compare-table`}>
          <thead>
            <tr className="text-ink-3">
              <th className="text-left col-head py-1" style={{ fontSize: 9.5 }}></th>
              <th className="text-right col-head py-1" style={{ fontSize: 9.5 }}>Ex O&P</th>
              <th className="text-right col-head py-1" style={{ fontSize: 9.5 }}>Quote vs ours</th>
              <th className="text-right col-head py-1" style={{ fontSize: 9.5 }}>Inc O&P</th>
              <th className="text-right col-head py-1" style={{ fontSize: 9.5 }}>Quote vs ours</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-line-soft" data-testid={`subby-quote-${qid}-compare-${r.key}`}>
                <td className="py-1.5 text-ink">{r.label}</td>
                <td className="py-1.5 text-right font-mono tnum text-ink" data-testid={`subby-quote-${qid}-compare-${r.key}-ex`}>{gbp(r.f.ex_oap)}</td>
                <td className="py-1.5 text-right"><Diff d={r.f.diff_ex} testid={`subby-quote-${qid}-compare-${r.key}-diff-ex`} /></td>
                <td className="py-1.5 text-right font-mono tnum text-ink" data-testid={`subby-quote-${qid}-compare-${r.key}-inc`}>{gbp(r.f.inc_oap)}</td>
                <td className="py-1.5 text-right"><Diff d={r.f.diff_inc} testid={`subby-quote-${qid}-compare-${r.key}-diff-inc`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-2 text-ink-4" style={{ fontSize: 10.5 }}>
        Quote {gbp(cmp.quote_amount)} · red = the quote is above our figure. Excludes {cmp.excludes}.
      </div>
      <CompareLinesDialog open={pick} onClose={() => setPick(false)} tenderId={tenderId} initialIds={quote.compare_line_ids || []} onSave={save} />
    </div>
  );
}
